/**
 * The sounds that run for as long as you are near them: snoring in the hall,
 * footfalls at a run, the karaoke through the navetta's wall, the yard's own
 * room tone, the DJ's set, the muurikka hissing and the projector's fan.
 *
 * Every one is rendered once into a buffer and played on repeat, so what is
 * tuned here is a loop's shape rather than a note's - see
 * `src/audio/buffers.ts`.
 */

/**
 * The main screen's one running joke.
 *
 * `ambient-snoring` is Kimmo asleep in his own bed, and a man asleep in his own
 * bed does more than breathe. The fart is a cue rather than part of the loop
 * for a reason worth writing down: the loop is 4.4 seconds long and plays for
 * as long as somebody sits on the READY screen, so anything inside it is heard
 * every 4.4 seconds - which is how a joke becomes wallpaper. Ten seconds is
 * far enough apart that the second one is still funny and the fifth one is
 * still a surprise, and it does not line up with the breathing.
 *
 * Presentation only, and menu-only at that: it cannot fire during a run, so it
 * is not in the fingerprint and never could be.
 */
export const SNORE = {
  /** Seconds of snoring between farts. Measured from the last one. */
  fartInterval: 10,
} as const;

/**
 * ==========================================================================
 *  THE RUN, AND HOW FAST IT IS GOING
 * ==========================================================================
 *  Feet are the one sound the player makes continuously, so they are a loop
 *  rather than a cue fired every couple of metres: a recording of somebody
 *  actually running has the weight shifting between the feet, and retriggering
 *  one step over and over throws all of that away.
 *
 *  The price of a loop is that it has one pace, and the player does not - the
 *  run spans 3.2 m/s at the start to 9.5 m/s with the vauhti meter full. So the
 *  loop is resampled: faster running plays the take faster. `src/audio/pace.ts`
 *  turns a ground speed into the rate and the level, and the mixer follows it.
 *
 *  Presentation only. Nothing here reaches the simulation.
 * ==========================================================================
 */
export const RUN_LOOP = {
  /**
   * The ground speed the recording plays at its own pace, in m/s. The take is
   * about 2.6 steps a second, which is roughly a person doing this.
   */
  referenceSpeed: 5.0,
  /**
   * How much of a change in speed reaches the playback rate.
   *
   * Deliberately not 1. Somebody running twice as fast does not take steps
   * twice as fast, they take longer ones - most of the speed is in the stride,
   * not the cadence. An exponent below 1 puts it there, and it is also what
   * keeps the whole 3.2 - 9.5 m/s band inside a rate that still sounds like a
   * person rather than a cartoon.
   */
  rateFollow: 0.55,
  /**
   * Hard limits on the rate. `playbackRate` moves pitch as well as pace, so
   * past these the runner stops being a person and starts being a chipmunk in
   * one direction and a diver in the other.
   */
  minRate: 0.72,
  maxRate: 1.45,
  /** Silent at or below this ground speed, and at full level from `loudSpeed`. */
  quietSpeed: 0.6,
  loudSpeed: 3.0,
  /**
   * Seconds for the rate and the level to catch up with the player, as an
   * exponential time constant. Short enough to feel connected to the sprint,
   * long enough that a stumble does not sound like a record being scratched.
   */
  smoothing: 0.12,
} as const;

/**
 * ==========================================================================
 *  THE KARAOKE, AND HOW FAR AWAY IT IS
 * ==========================================================================
 *  Somebody has been on the mic in the juhlasali since before Kimmo woke up,
 *  and the run is a long walk towards them. That is the whole reason this cue
 *  is placed rather than simply switched on at a checkpoint: the music is how
 *  you know you are getting closer, and a checkpoint would make it a door.
 *
 *  The hall has two things making noise at its two ends: the karaoke rig just
 *  inside the door, and the projector's clip on the far wall, over Kimmo's
 *  table (`PROJECTOR` below). So the run walks towards the singing right up
 *  to the door, passes the rig, and then walks *away* from it towards the
 *  screen - the last fifteen metres are a handover from one to the other,
 *  and at the chair the clip is the louder of the two. The singing is still
 *  there at the chair; it is just no longer the thing in front of you.
 *
 *  Two things change with the listener's position, and `src/audio/karaoke.ts`
 *  computes both from these numbers:
 *
 *    * how much of it reaches you - distance, and
 *    * how much *top* is left of it - a navetta wall in the way turns singing
 *      into a bass line you can only just make out.
 *
 *  Presentation only. Nothing here reaches the simulation, so all of it can be
 *  retuned without touching a single leaderboard time.
 * ==========================================================================
 */
export const KARAOKE = {
  /**
   * The rig itself: just inside the juhlasali's door from the eteinen, against
   * the west wall - the doorway `sali-lansi` and `eteinen-ita` leave between
   * them in src/config/level.ts, half a metre into the room. It is the first
   * thing in the hall you pass, and everything after it is a walk away from
   * it. `karaoke.test.ts` holds it to that doorway.
   */
  source: { x: 132.0, z: -34.35 },
  /**
   * Full level once this close, in metres - and, past it, the distance the
   * inverse-distance law is measured from: at twice this the sound is half its
   * level, at four times a quarter. That is what decides how much of the
   * singing is left at the far end of the hall, so it is the number to move if
   * the rig is drowning the screen rather than the other way round.
   */
  full: 6.5,
  /** Gone entirely beyond this - out by the cars, where the run starts. */
  silent: 52.0,
  /** Through the navetta's wall, from the yard. */
  outside: { gain: 0.32, cutoff: 620 },
  /** Same building, nothing in the way. */
  inside: { gain: 1.0, cutoff: 14000 },
  /**
   * The navetta's footprint, in world XZ: the outer faces of the four
   * `navetta-*` walls in src/config/level.ts. Inside it is inside, which
   * includes the eteinen and the WCs; the porch out front is not.
   * `karaoke.test.ts` holds this to the level's own geometry.
   */
  venue: { minX: 125.65, maxX: 155.75, minZ: -45.45, maxZ: -29.65 },
  /**
   * Seconds for the mix to catch up with the listener, as an exponential time
   * constant. Long enough that stepping through the door reads as a door
   * opening rather than a cut, short enough that it is over before you have
   * taken three more steps.
   */
  smoothing: 0.28,
  /** Butterworth, i.e. no resonant bump at the corner. */
  resonance: 0.7,
} as const;

/**
 * ==========================================================================
 *  THE YARD, AND WHERE IT STOPS
 * ==========================================================================
 *  `ambient-morning` is the yard at nine in the morning, and the yard at nine
 *  in the morning is birds. It runs from the first step of the run to the
 *  last, which makes it the one sound the player never gets a break from - so
 *  what it is matters twice: anything continuous in it stops being heard and
 *  starts being *sat through*, and it masks whatever else is continuous, which
 *  is the run loop, which is the player's own feet.
 *
 *  It is also outdoor sound, and the run ends indoors. So it is mixed off the
 *  listener the same way the karaoke is, and for the same reason - the navetta
 *  has a wall, and a wall is heard. The difference is which side you are on:
 *  the karaoke is a thing in a room you are walking towards, and this is the
 *  room you are walking out of.
 *
 *  Presentation only. Nothing here reaches the simulation.
 * ==========================================================================
 */
export const YARD = {
  /** Out in it, with nothing over your head. */
  outside: { gain: 1.0, cutoff: 14000 },
  /**
   * Indoors: through a wall, from the far side of a closed door. Not silent -
   * a navetta full of sleeping people is not soundproofed, and cutting the
   * birds at the threshold would be the door-as-a-switch the karaoke's own
   * comment argues against - but well down, and with the top gone.
   */
  inside: { gain: 0.18, cutoff: 780 },
  /**
   * Everywhere in the level with a roof over it, in world XZ. Being in none of
   * them is being in the yard, which is the whole test.
   *
   * Boxes rather than a room-by-room walk of the walls: what the birds care
   * about is the building, and every room of a building is under the same
   * roof. `yard.test.ts` holds each of these to the level's own walls, so a
   * wall that moves cannot leave a corner of a room out in the sunshine.
   */
  roofs: [
    /** Einon pirtti, makuuhuone: `pirtti-seina-*` and the wall to the hall. */
    { minX: -0.3, maxX: 10.3, minZ: -6.3, maxZ: 4.3 },
    /** Pirtin käytävä, from that wall to the ulko-ovi: `kaytava-*`. */
    { minX: 10.0, maxX: 33.3, minZ: 0.1, maxZ: 4.55 },
    /** The juhlanavetta - the same footprint the karaoke is bounded by. */
    KARAOKE.venue,
  ],
  /**
   * Seconds for the mix to catch up with the listener. Slower than the
   * karaoke's: that one is a door you run through in one stride, and this is
   * the outside world receding behind you.
   */
  smoothing: 0.4,
} as const;

/**
 * ==========================================================================
 *  THE PA IN THE XLMOTO TENT, AND WHY IT IS HEARD FROM A BEDROOM
 * ==========================================================================
 *  Somebody set a pair of decks up in the sponsor's pop-up halfway down the
 *  yard, and has not stopped. It is the second placed sound in the game and
 *  the mirror image of the first: the karaoke is a rig in a room at the end of
 *  the run, and this is a rig in the open air in the middle of it.
 *
 *  That difference is the whole of this block, because it changes which of the
 *  two numbers does the work.
 *
 *  A KARAOKE RIG GOES QUIET WITH DISTANCE. A PA DOES NOT - IT GOES DULL.
 *  Anybody who has walked towards a field party knows what it sounds like from
 *  the road: not a quiet version of the music, but the bass line of it, with
 *  everything above the kick left behind somewhere in the last two hundred
 *  metres. Air absorbs the top and a wall absorbs the top; neither does much
 *  to 60 Hz. So the *cutoff* is the number that does the work, and the level
 *  under it falls off the way a level actually does across open ground -
 *  steeply, and steepest of all in the half of the yard nobody is near yet.
 *  What the run opens with is a thump under the birds with no tune in it at
 *  all, and quiet enough that you have to stop moving to hear it.
 *
 *  Which is what makes it worth having. Kimmo wakes up 97 m from this thing,
 *  and from the first frame of the run there is something out in the yard
 *  keeping time. It is still barely there at the ulko-ovi, it resolves into
 *  music down the parking lot, and it is not everything it is going to be
 *  until the last bay is behind you - and none of that is a cue, a trigger or
 *  a checkpoint, it is one loop and a distance. The karaoke's own comment
 *  makes this argument for the last thirty metres of the run; this is the same
 *  argument made for the first hundred.
 *
 *  AND A PA POINTS SOMEWHERE, which is the second sum and the reason the yard
 *  does not sound the same on both sides of the tent. The stacks stand either
 *  side of the open west wall, aimed out of it - so everything between the
 *  tent and the pirtti is *in front of* them and everything from the ansalanka
 *  to the navetta is behind. That is not a fudge to keep the mix tidy: behind
 *  a speaker cabinet you get a fraction of the level and almost none of the
 *  top, and it is the difference between walking up to a stage and walking
 *  round the back of one.
 *
 *  It buys the run its shape. The whole approach from the bed to the tent is
 *  down the throw and grows the entire way; the moment the line passes the
 *  tent's corner and turns for the ansalanka it is round the back of the rig,
 *  and eight metres later the set is a thump behind you. Which is what the
 *  yard past that point needs: Hannu comes out of his palju shouting, and a
 *  man shouting at you from four metres has to be the loudest thing in the
 *  world - with the party still going on behind him, because it is.
 *  `teltta.test.ts` holds both halves of that.
 *
 *  `src/audio/teltta.ts` computes both numbers from these; `teltta.test.ts`
 *  walks the route and holds the shape of the approach to it, and holds
 *  `source` to the tent's own collider so the two cannot drift apart.
 *
 *  Presentation only. Nothing here reaches the simulation, so all of it can be
 *  retuned without touching a single leaderboard time.
 * ==========================================================================
 */
export const TELTTA_DJ = {
  /**
   * The middle of the XLMOTO tent's footprint - `TELTTA` in
   * src/config/level.ts, which is 3 m square from (96.5, -19.5). The stacks
   * stand either side of the open west wall and the desk between them, so the
   * centre of the box is as close to "where the noise comes from" as one point
   * gets. `teltta.test.ts` holds it there.
   */
  source: { x: 98.0, z: -18.0 },
  /**
   * Full level once this close, in metres - and the number is the end of the
   * lot rather than an arm's length from the stacks. The aisle comes out of
   * the last bay at about (92, -13) - `PARKING.end` in src/config/level.ts,
   * and the `PARKING_END` checkpoint's own box - which is eight metres off the
   * source, so the plateau starts exactly where the cars stop. Everything
   * before that is still on the way up; what is left after it is the throw
   * lining up rather than the distance closing. `teltta.test.ts` holds the
   * plateau to that checkpoint.
   *
   * Unlike the karaoke's `full`, this one is *only* a plateau, because there
   * is no second law underneath it. See `telttaMix`.
   */
  full: 8.0,
  /**
   * Gone entirely beyond this. It has to be *well* past the spawn - the run
   * starts 97 m away and the point of the whole block is that you can hear it
   * from there - so the level's own length is the floor under this number
   * rather than a taste decision, and the number is a good deal over it so
   * that the bedroom is inside the ramp rather than on the end of it.
   */
  silent: 150.0,
  /**
   * How fast the ramp falls, as a power. *Over* the karaoke's own squaring,
   * and that is the correction this number carries: a gentle ramp across a
   * hundred metres of open ground is a PA that is already half of itself at
   * the ulko-ovi and has nowhere left to grow for the whole of the lot, which
   * is a party you hear from the neighbour's field rather than one you run
   * towards. A cube-ish law puts the growth where the run is - the lot is
   * where it turns into music and the last bay is where it arrives - and it
   * leaves the far end of the level the thump it should be.
   *
   * This is the number that decides how loud the bedroom is, and it is set so
   * that what reaches the bed sits well under the birds rather than over them:
   * there, but only if you stop and listen for it. See `teltta.test.ts`, which
   * holds it there rather than to a figure.
   */
  rolloff: 2.8,
  /**
   * The lowpass at the tent and at the far end of the level. Interpolated
   * *geometrically* between the two - ears hear frequency in ratios, so a
   * linear sweep between 130 Hz and 13 kHz spends its whole first half above
   * 6 kHz and none of the approach sounds like an approach.
   */
  nearCutoff: 13000,
  farCutoff: 130,
  /**
   * Which way the stacks are aimed, as a unit vector in world XZ. West, out of
   * the wall that came off - see `TELTTA.open` in src/config/level.ts, which
   * is the same direction and the reason this one is what it is.
   */
  axis: { x: -1, z: 0 },
  /**
   * What is left of the set directly behind the cabinets, before distance has
   * had its say. A fifth: a stack throws forward, and the back of one is the
   * cabinet.
   */
  back: 0.22,
  /**
   * How sharply the throw narrows, as a power on the half-angle. Around 1 is a
   * broad cardioid - loud out front, half of that off to the side, and the back
   * of the cabinet at `back`. Push it up and the tent becomes a searchlight
   * that is only audible on one line down the yard.
   */
  aimFocus: 1.1,
  /** Out in it, with nothing over your head. */
  outside: { gain: 1.0, cutoff: 16000 },
  /**
   * Indoors, in the pirtti at the start or the navetta at the end. Well down
   * but nothing like as far down as `YARD.inside` takes the birds, and with a
   * far harder ceiling on the top: a wall takes the cymbals off a PA and
   * leaves the kick, which is why the thing you hear through one is a beat and
   * not a song.
   */
  inside: { gain: 0.55, cutoff: 260 },
  /**
   * Seconds for the mix to catch up with the listener. Slower than the
   * karaoke's, which is a doorway you cross in one stride: this is a hundred
   * metres of open yard, and nothing about it should arrive in a hurry.
   */
  smoothing: 0.5,
} as const;

/**
 * ==========================================================================
 *  THE MUURIKKA, AND WHY IT IS THE SHORTEST SOUND IN THE YARD
 * ==========================================================================
 *  A metre of steel plate on a gas ring out on the kivetys, with a hall's
 *  worth of breakfast on it. It is the third placed sound in the game, and it
 *  is the one that answers the question the other two do not: what does a
 *  sound with *no bass in it at all* do with distance?
 *
 *  It stops. That is the whole block.
 *
 *  The karaoke carries fifty metres and the tent carries a hundred and fifty,
 *  and both carry because both have something under 200 Hz to carry with. Fat
 *  in a pan has nothing down there - the take measured 34 dB down at 60 Hz
 *  against its own level, and its weight is up at ten kilohertz. A sound like
 *  that is spent at conversational range: you hear it when you are at the pan,
 *  you hear it walking up to the pan, and eighteen metres out there is nothing
 *  left of it to hear. So `silent` is a fifth of the karaoke's and an
 *  eleventh of the tent's, and that is not a taste decision - it is what the
 *  spectrum of the recording allows.
 *
 *  WHICH MAKES IT THE ONE PLACED SOUND THAT IS ABOUT A PLACE YOU STOP AT.
 *  The other two are heard while running past them. This one is heard while
 *  standing still: `AAMUPALA` makes the player stand within `AAMUPALA.reach`
 *  of the plate and press three keys, and those are the seconds this exists
 *  for. `full` is that reach, to the centimetre - the game's own definition of
 *  "at the pan" is the mixer's, and `muurikka.test.ts` holds the two together.
 *
 *  AND IT IS THE ONE THING IN THE YARD THE BLACKOUT DOES NOT TOUCH. The
 *  karaoke and the tent both come off the same tolppa the bemari's cable was
 *  in and both stop mid-bar; this is a gas ring with a bottle under it. So the
 *  last stretch of a run that pulled the cable is a dark yard with birds in it
 *  and one thing still cooking - see `Game.applyEvents`, which stops the other
 *  two and deliberately does not stop this.
 *
 *  `src/audio/muurikka.ts` computes both numbers from these.
 *
 *  Presentation only. Nothing here reaches the simulation, so all of it can be
 *  retuned without touching a single leaderboard time.
 * ==========================================================================
 */
export const MUURIKKA_SIHINA = {
  /**
   * The plate's own centre - `MUURIKKA` in src/config/level.ts, which is also
   * where `AAMUPALA_SPEC.station` is taken from. Two hand-typed numbers in two
   * files, the way `TELTTA_DJ.source` is; `muurikka.test.ts` is what stops
   * them drifting apart when the pan slides along the wall.
   */
  source: { x: 138.6, z: -28.35 },
  /**
   * Full level once this close, in metres - and, past it, the distance the
   * inverse-distance law is measured from, exactly as in `KARAOKE.full`.
   *
   * It is `AAMUPALA.reach`, which is the radius the game itself calls being at
   * the muurikka: inside it the boxes come up, outside it they do not. Picking
   * any other number would mean the sound arriving at full a moment before or
   * after the thing it belongs to does, which is the one mistake a placed sound
   * of this range would actually be heard making.
   */
  full: 1.9,
  /**
   * Gone entirely beyond this. Eighteen metres puts the far edge back at about
   * the veräjä, which is where the cook shouts from - so the sizzle fades in
   * over the same stretch of kivetys his line is heard across, and the pan is
   * audible for the whole of the approach it is at the end of.
   */
  silent: 18.0,
  /**
   * The lowpass at the plate and at the edge of its range. Interpolated
   * geometrically, like the tent's, because ears hear frequency in ratios.
   *
   * Read off the *ramp* rather than off the level, which is the one place this
   * sum differs from `telttaMix`. A PA loses its top long before it loses its
   * level, so there the two share a term; a pan does the opposite. What you
   * hear walking away from a muurikka is the same bright hiss getting quieter,
   * right up until it is nearly gone - and only in the last few metres of range
   * does it stop being individual fat spitting and turn into a smear. Tying the
   * cutoff to the level instead would have it dull at nine metres, which is a
   * grill heard through something rather than one across a yard.
   */
  nearCutoff: 15000,
  farCutoff: 3000,
  /** Out on the kivetys, with nothing over your head. */
  outside: { gain: 1.0, cutoff: 16000 },
  /**
   * Indoors, and by a distance the hardest wall of the three. Two reasons, and
   * both are particular to this sound rather than to walls in general.
   *
   * The pan stands 1.3 m off the navetta's south wall, which means somebody in
   * the eteinen is *nearer to it than the player is allowed to stand* - so
   * anything but a hard term would put a working grill inside the room, and
   * put it there louder than it is out on the paving. And a rendered blockwork
   * wall takes the top off everything, which for a PA leaves a kick drum and
   * for a hiss leaves nothing at all: there is no low end in this sound for a
   * wall to hand back. Both of those say the same thing, so it says it once and
   * hard.
   */
  inside: { gain: 0.1, cutoff: 420 },
  /**
   * Seconds for the mix to catch up with the listener. The shortest in the
   * game, because this is the shortest range in the game: eighteen metres from
   * silent to full is a couple of seconds at running speed, and a mix that
   * ambles after it would have the pan still arriving while the plate is being
   * filled.
   */
  smoothing: 0.2,
} as const;

/**
 * The projector at the back of the juhlasali, and the one noise it makes.
 *
 * `cueAt` is a timestamp *inside the clip* on the valkokangas
 * (src/render/video/valkokangas.mp4), not a time in the run: fifteen seconds
 * in, something happens on the screen and somebody in the room has an opinion
 * about it. The clip loops, so it comes round every half minute for as long as
 * the page is open - which is the joke, and is also why it is heard only by
 * somebody standing in the building it happens in.
 *
 * Presentation only, and wall-clock: the projector has been running since the
 * page loaded and does not rewind because a run did. Nothing here can reach
 * the simulation - see src/render/Projection.ts.
 */
export const PROJECTOR = {
  cueAt: 15.0,
  /**
   * Where the picture is, in world XZ: the middle of the valkokangas
   * (`VALKOKANGAS` in src/config/level.ts). The clip's own soundtrack is mixed
   * off this the same way the karaoke is mixed off `KARAOKE.source` - two
   * things making noise at the same end of the same hall, each from its own
   * spot rather than both from the singer's.
   */
  source: { x: 150.2, z: -36.2 },
  /**
   * The soundtrack's ceiling, standing in front of it, before the room has had
   * its say.
   *
   * The rig stands at the door and the screen is fourteen metres past it over
   * Kimmo's table, so the singing wins by distance at the door and the clip
   * wins at the chair. Keep this independent of karaoke volume adjustments;
   * `karaoke.test.ts` pins the handover.
   */
  gain: 0.8,
} as const;
